import type { ReactElement } from "react";
import {
  AlertTriangle,
  BadgeDollarSign,
  BarChart3,
  BookOpenCheck,
  BookOpenText,
  CalendarClock,
  Coins,
  DollarSign,
  Globe,
  History,
  LayoutGrid,
  MousePointer2,
  NotebookText,
  NotepadText,
  Package,
  Percent,
  Receipt,
  Repeat2,
  ShoppingCart,
  Target,
  UserPlus,
  Users,
  Wallet,
  Activity,
  Boxes,
  LucideIcon,
} from "lucide-react";


const PAGEVIEW_STYLE_ICONS: LucideIcon[] = [
  NotebookText,
  BookOpenText,
  NotepadText,
  BookOpenCheck,
];

const SESSION_STYLE_ICONS: LucideIcon[] = [History, CalendarClock, Repeat2];

function titleVariantBucket(title: string, modulo: number): number {
  let h = 0;
  for (let i = 0; i < title.length; i += 1) {
    h = (h * 31 + title.charCodeAt(i)) >>> 0;
  }
  return modulo > 0 ? h % modulo : 0;
}

/**
 * Picks a Lucide icon for a KPI card from the title (and optional id) so dashboards
 * share consistent, less repetitive iconography.
 */
export function getDashboardKpiLucideIconComponent(title: string): LucideIcon {
  const n = title.toLowerCase();

  if (n.includes("churn") || n.includes("at-risk") || n.includes("at risk")) {
    return AlertTriangle;
  }

  if (n.includes("roas")) {
    return DollarSign;
  }
  if (n.includes("cpc") || n.includes("cost per click")) {
    return DollarSign;
  }
  if (
    n.includes("cpa") ||
    n.includes("cpp") ||
    n.includes("cost per acquisition") ||
    n.includes("cost per purchase") ||
    n.includes("cost per conv")
  ) {
    return DollarSign;
  }
  if (n.includes("refund")) {
    return DollarSign;
  }
  if (n.includes("spend") || (n.includes("cost") && !n.includes("per"))) {
    return DollarSign;
  }
  if (
    n.includes("revenue") ||
    n.includes("gross") ||
    n.includes("income") ||
    n.includes("payout")
  ) {
    return DollarSign;
  }
  if (n.includes("profit") || n.includes("tax")) {
    return DollarSign;
  }
  if (n.includes("aov") || n.includes("avg. order") || n.includes("average order")) {
    return DollarSign;
  }
  if (n.includes("cltv") || n.includes("lifetime value")) {
    return DollarSign;
  }

  if (n.includes("pageview") || n.includes("page view")) {
    return PAGEVIEW_STYLE_ICONS[titleVariantBucket(title, 4)];
  }
  if (n.includes("impression")) {
    return NotebookText;
  }

  if (n.includes("session duration") || n.includes("avg session duration") || n.includes("session time")) {
    return CalendarClock;
  }
  if (n.includes("sessions per") || n.includes("session/user") || n.includes("sessions per user")) {
    return Repeat2;
  }
  if (n.includes("session")) {
    return SESSION_STYLE_ICONS[titleVariantBucket(title, 3)];
  }

  if (n.includes("bounce") || n.includes("ctr") || n.includes("drop-off") || n.includes("drop off")) {
    return titleVariantBucket(title, 2) === 0 ? NotepadText : NotebookText;
  }
  if (n.includes("rate") || n.includes("percent") || n.includes("%")) {
    return Percent;
  }

  if (n.includes("conversion") && !n.includes("click")) {
    return BookOpenCheck;
  }
  if (n.includes("click")) {
    return Repeat2;
  }

  if (n.includes("order") && !n.includes("avg") && !n.includes("average") && !n.includes("value")) {
    return ShoppingCart;
  }

  if (n.includes("inventory") || n.includes("stock") || n.includes("sku") || n.includes("fulfillment")) {
    return Package;
  }
  if (n.includes("product") && n.includes("catalog")) {
    return Package;
  }

  if (n.includes("new customer") || n.includes("user plus") || n.includes("signup")) {
    return UserPlus;
  }
  if (n.includes("customer") || n.includes("users") || n.includes("user") || n.includes("audience")) {
    return Users;
  }

  if (n.includes("event") || n.includes("key event")) {
    return History;
  }

  if (n.includes("channel") && (n.includes("top") || n.includes("best"))) {
    return BarChart3;
  }

  return BookOpenText;
}

export function renderDashboardKpiLucideIcon(title: string, size: number): ReactElement {
  const Icon = getDashboardKpiLucideIconComponent(title);
  return <Icon size={size} />;
}

const DEFAULT_ICON_POOL: LucideIcon[] = [
  BadgeDollarSign,
  DollarSign,
  Coins,
  Wallet,
  ShoppingCart,
  Receipt,
  Package,
  Boxes,
  Users,
  UserPlus,
  Globe,
  MousePointer2,
  Percent,
  Target,
  Activity,
  History,
  CalendarClock,
  Repeat2,
  NotebookText,
  NotepadText,
  BookOpenText,
  BookOpenCheck,
  BarChart3,
  LayoutGrid,
];

const DASHBOARD_ICON_POOLS: Record<string, LucideIcon[]> = {
  metaAds: [
    BadgeDollarSign,
    Coins,
    MousePointer2,
    Target,
    Globe,
    Users,
    Activity,
    Percent,
    ShoppingCart,
    BarChart3,
    NotebookText,
    CalendarClock,
  ],
  googleAds: [
    Wallet,
    DollarSign,
    Target,
    MousePointer2,
    Globe,
    Users,
    Activity,
    Receipt,
    Percent,
    ShoppingCart,
    BarChart3,
    History,
  ],
  webAnalytics: [
    Globe,
    Users,
    History,
    CalendarClock,
    Repeat2,
    NotebookText,
    NotepadText,
    BookOpenText,
    BookOpenCheck,
    Activity,
    Percent,
    MousePointer2,
  ],
  salesProduct: [
    DollarSign,
    ShoppingCart,
    Users,
    Wallet,
    Package,
    Receipt,
    Boxes,
    BarChart3,
    Activity,
    Globe,
    Target,
  ],
};

export function getUniqueDashboardKpiLucideIconComponents(
  titles: string[],
  scope = "default",
): LucideIcon[] {
  const pool = DASHBOARD_ICON_POOLS[scope] ?? DEFAULT_ICON_POOL;
  const used = new Set<LucideIcon>();

  return titles.map((title, index) => {
    const preferred = getDashboardKpiLucideIconComponent(title);
    if (!used.has(preferred)) {
      used.add(preferred);
      return preferred;
    }

    const poolIndex = titleVariantBucket(`${scope}:${title}:${index}`, pool.length);
    for (let offset = 0; offset < pool.length; offset += 1) {
      const candidate = pool[(poolIndex + offset) % pool.length];
      if (!used.has(candidate)) {
        used.add(candidate);
        return candidate;
      }
    }

    return preferred;
  });
}

export function renderUniqueDashboardKpiLucideIcons(
  titles: string[],
  size: number,
  scope = "default",
): ReactElement[] {
  return getUniqueDashboardKpiLucideIconComponents(titles, scope).map((Icon, index) => (
    <Icon key={`${scope}-${titles[index]}-${index}`} size={size} />
  ));
}
